import * as React from 'react';
import { Tooltip, Polygon } from 'react-leaflet';
import { LatLng } from 'leaflet';
import {
  EditablePolygon,
  GeotiffOverlay,
  FieldClickEventArgs,
  RasterMapReference
} from './';
import { inferRasterProperties } from '../utils';
import { GeotiffRender } from '../utils/renderGeotiffRaster';

const defaultPolygonStyle = {
  color: '#3388FF',
  fill: true,
  weight: 2
};

const selectedPolygonStyle = {
  color: '#24D986',
  fill: true,
  weight: 4
};

const editingPolygonStyle = {
  color: '#ff1744',
  fill: true,
  weight: 4
};

interface Props {
  field: Field;
  zindex?: number;
  selected: boolean;
  editable?: boolean;
  layer?: RasterDocument | null;
  onClick?: (args: FieldClickEventArgs) => void;
  onReferenceClick?: (args: FieldClickEventArgs) => void;
  onEdit?: (newGeometry: PolygonGeometry) => void;
}

interface State {
  loading: boolean;
  render: GeotiffRender | null;
}

function mapGeometryToPolygon(field: Field): LatLng[][] {
  return field.geometry.coordinates.map(polygon =>
    polygon.map(point => new LatLng(point[1], point[0]))
  );
}

function mapPolygonToGeometry(positions: LatLng[][]): PolygonGeometry {
  return {
    type: 'Polygon',
    coordinates: positions.map(polygon => [
      ...polygon.map(point => [point.lng, point.lat]),
      [polygon[0].lng, polygon[0].lat]
    ])
  };
}

export default class FieldOverlay extends React.Component<Props, State> {
  state = {
    loading: false,
    render: null
  };

  handleEditableChange = (newPositions: LatLng[][]) => {
    const { onEdit } = this.props;

    if (!onEdit) {
      console.warn('Field edited but nobody to listen');
      return;
    }

    onEdit(mapPolygonToGeometry(newPositions));
  };

  handleLayerRender = (render: GeotiffRender) => {
    this.setState({ render, loading: false });
  };

  handleObjectClick = () => {
    const { onClick, field, layer } = this.props;
    const { render } = this.state;
    if (onClick) {
      onClick({ field, layer, render });
    }
  };

  handleReferenceClick = () => {
    const { onReferenceClick, field, layer } = this.props;
    const { render } = this.state;
    if (onReferenceClick) {
      onReferenceClick({ field, layer, render });
    }
  };

  renderReference() {
    const { field, selected } = this.props;
    const { render } = this.state;

    if (!render) {
      return null;
    }

    return (
      <RasterMapReference
        open={selected}
        render={render || null}
        totalArea={field.area}
        onClick={this.handleReferenceClick}
      />
    );
  }

  renderLayer(layer: RasterDocument) {
    const { field } = this.props;
    const rasterProps = inferRasterProperties(layer);

    return (
      <GeotiffOverlay
        id={layer.id}
        geotiffUrl={layer.rasterUrl}
        geotiffType={rasterProps.type}
        inputDomain={rasterProps.inputDomain}
        outputDomain={rasterProps.outputDomain}
        nodata={rasterProps.nodata}
        geometry={field.geometry}
        customBins={layer.bins}
        onClick={this.handleObjectClick}
        onRender={this.handleLayerRender}
      />
    );
  }

  renderStatic() {
    const { field, selected } = this.props;
    const styleProps = selected ? selectedPolygonStyle : defaultPolygonStyle;
    return (
      <Polygon
        key={field.id}
        positions={mapGeometryToPolygon(field)}
        onclick={this.handleObjectClick}
        {...styleProps}
      >
        <Tooltip direction="top" permanent={true}>
          <span>
            <strong>{field.name}</strong> ({field.currentCrop})
          </span>
        </Tooltip>
      </Polygon>
    );
  }

  renderEditable() {
    const { field } = this.props;

    return (
      <EditablePolygon
        key={field.id}
        positions={mapGeometryToPolygon(field)}
        onChange={this.handleEditableChange}
        {...editingPolygonStyle}
      />
    );
  }

  renderOverlay() {
    const { editable, layer } = this.props;
    if (editable) {
      return this.renderEditable();
    } else if (layer) {
      return this.renderLayer(layer);
    } else {
      return this.renderStatic();
    }
  }

  render() {
    return (
      <>
        {this.renderOverlay()}
        {this.renderReference()}
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FieldOverlay.tsx